/**
 * Monero wallet integration via monero-ts WASM for Cashero.
 *
 * Wraps the monero-ts library in a clean API for creating/syncing wallets,
 * scanning for deposits, and sending XMR — all client-side in the browser.
 *
 * The WASM module is dynamically imported to avoid blocking the initial page load.
 * New wallets don't need sync (no history). Imported wallets sync from restore height.
 */

import { generateMoneroKeys } from '@/lib/monero/keys';

// Types for our wallet abstraction
export interface MoneroWalletConfig {
  seed: string;
  address: string;
  viewKey: string;
  spendKey: string;
  nodeUri: string;
  restoreHeight: number;
}

export interface SyncProgress {
  height: number;
  startHeight: number;
  endHeight: number;
  percentDone: number;
  message: string;
}

export interface IncomingTransfer {
  txHash: string;
  amount: number; // piconero
  subaddressIndex: number;
  confirmations: number;
  timestamp: number;
}

export type SyncListener = (progress: SyncProgress) => void;

/**
 * Dynamic loader for monero-ts.
 * The WASM module is large (~5.7MB), so we lazily import it on first use.
 * Only loaded when mint operator mode is active.
 *
 * Uses the esm.sh CDN URL directly to avoid bare-specifier resolution at
 * runtime, while the @vite-ignore comment prevents bundler static analysis
 * from following into monero-ts's Node.js dependencies.
 */
const MONERO_TS_URL = 'https://esm.sh/monero-ts@0.11.10?bundle';

let moneroTsModule: any = null;
let moneroTsError: Error | null = null;

/**
 * Configure monero-ts WASM module paths to load from local files.
 * The WASM files are copied to /monero/ by the build process.
 */
export function configureWasmPaths(moneroTs?: any): void {
  if (typeof globalThis === 'undefined') return;
  // Set the WASM path for the Emscripten module loader
  (globalThis as any).monero_wasm_path = '/monero/';
  // LibraryUtils for monero-ts
  if (moneroTs?.LibraryUtils?.setStaticWasmPath) {
    moneroTs.LibraryUtils.setStaticWasmPath('/monero/');
  }
}

async function getMoneroTs(): Promise<any> {
  if (moneroTsError) throw moneroTsError;
  if (moneroTsModule) return moneroTsModule;

  try {
    // @vite-ignore — prevent bundler from resolving at build time
    const mod = await import(/* @vite-ignore */ MONERO_TS_URL);
    moneroTsModule = mod;
    configureWasmPaths(mod);
    return moneroTsModule;
  } catch (err) {
    moneroTsError = new Error(
      `Failed to load Monero wallet module: ${(err as Error).message}. ` +
      `This may take a moment to download on first use (~1MB).`,
    );
    throw moneroTsError;
  }
}

/**
 * Create a new Monero wallet (keys-only, no sync needed).
 * This generates a fresh seed phrase, address, view key, and spend key.
 * Uses pure-JS key generation — no WASM module required.
 * Since it's a new wallet, no blockchain sync is required.
 */
export async function createNewWallet(nodeUri: string): Promise<MoneroWalletConfig> {
  // Pure JS key generation — no WASM needed for key creation
  const keys = generateMoneroKeys();

  return {
    seed: keys.seed,
    address: keys.address,
    viewKey: keys.viewKey,
    spendKey: keys.spendKey,
    nodeUri,
    restoreHeight: 0, // new wallet
  };
}

/**
 * Create a view-only wallet for checking incoming deposits.
 * This only needs the address and view key — no spend key required.
 */
export async function createViewOnlyWallet(
  address: string,
  viewKey: string,
  nodeUri: string,
  restoreHeight: number,
): Promise<any> {
  const moneroTs = await getMoneroTs();

  const wallet = await moneroTs.createWalletFull({
    password: 'cashero-viewonly',
    networkType: moneroTs.MoneroNetworkType.MAINNET,
    server: { uri: nodeUri },
    restoreHeight: restoreHeight || 0,
  });

  return wallet;
}

/**
 * Create a full wallet from seed (for sending XMR).
 * Syncs from the provided restore height.
 */
export async function createFullWallet(
  seed: string,
  nodeUri: string,
  restoreHeight: number,
): Promise<any> {
  const moneroTs = await getMoneroTs();

  const wallet = await moneroTs.createWalletFull({
    password: 'cashero-wallet',
    networkType: moneroTs.MoneroNetworkType.MAINNET,
    server: { uri: nodeUri },
    seed,
    restoreHeight: restoreHeight || 0,
  });

  return wallet;
}

/**
 * Sync a wallet with the daemon, with progress callbacks.
 * For new wallets (no restore height), this completes instantly.
 * For imported wallets, this scans from the restore height.
 */
export async function syncWallet(
  wallet: any,
  onProgress?: SyncListener,
): Promise<void> {
  if (!wallet || !wallet.sync) return;

  try {
    await wallet.sync(
      new (await getMoneroTs()).MoneroWalletListener(
        (height: number, startHeight: number, endHeight: number, percentDone: number, message: string) => {
          onProgress?.({ height, startHeight, endHeight, percentDone, message });
        },
      ),
    );
  } catch (err) {
    console.warn('Wallet sync error (may be normal for new wallets):', err);
  }
}

/**
 * Start background syncing at a given interval.
 */
export async function startBackgroundSync(
  wallet: any,
  intervalMs: number = 30000,
): Promise<void> {
  if (!wallet || !wallet.startSyncing) return;
  try {
    await wallet.startSyncing(intervalMs);
  } catch (err) {
    console.warn('Background sync error:', err);
  }
}

/**
 * Stop background syncing.
 */
export async function stopBackgroundSync(wallet: any): Promise<void> {
  if (!wallet || !wallet.stopSyncing) return;
  try {
    await wallet.stopSyncing();
  } catch {
    // Ignore
  }
}

/**
 * Get the wallet's balance in piconero.
 */
export async function getBalance(wallet: any): Promise<number> {
  if (!wallet) return 0;
  try {
    const balance = await wallet.getBalance();
    return Number(balance);
  } catch {
    return 0;
  }
}

/**
 * Get the wallet's primary address.
 */
export async function getAddress(wallet: any): Promise<string> {
  if (!wallet) return '';
  return wallet.getAddress(0, 0);
}

/**
 * Create a subaddress at a specific index (for deposit tracking).
 */
export async function createSubaddress(
  wallet: any,
  accountIndex: number = 0,
  subaddressIndex: number,
): Promise<string> {
  if (!wallet) throw new Error('Wallet not initialized');

  // monero-ts creates subaddresses on demand
  return wallet.getAddress(accountIndex, subaddressIndex);
}

/**
 * Scan for incoming deposits to a specific subaddress.
 * Checks the wallet's incoming transfers and returns any that match.
 */
export async function scanForDeposits(
  wallet: any,
  subaddressIndex: number,
  minConfirmations: number = 1,
): Promise<IncomingTransfer[]> {
  if (!wallet) return [];

  try {
    const transfers = await wallet.getTransfers();
    const incoming: IncomingTransfer[] = [];

    const allIncoming = transfers.incoming ?? [];

    for (const tx of allIncoming) {
      if (tx.subaddressIndex === subaddressIndex) {
        incoming.push({
          txHash: tx.txHash ?? tx.hash,
          amount: Number(tx.amount),
          subaddressIndex: tx.subaddressIndex,
          confirmations: tx.confirmations ?? 0,
          timestamp: tx.timestamp ?? Date.now(),
        });
      }
    }

    return incoming;
  } catch (err) {
    console.warn('Error scanning for deposits:', err);
    return [];
  }
}

/**
 * Scan for deposits across all monitored subaddresses in one pass.
 */
export async function scanAllIncoming(
  wallet: any,
): Promise<IncomingTransfer[]> {
  if (!wallet) return [];

  try {
    const transfers = await wallet.getTransfers();
    const incoming: IncomingTransfer[] = [];

    const allIncoming = transfers.incoming ?? [];

    for (const tx of allIncoming) {
      incoming.push({
        txHash: tx.txHash ?? tx.hash,
        amount: Number(tx.amount),
        subaddressIndex: tx.subaddressIndex ?? 0,
        confirmations: tx.confirmations ?? 0,
        timestamp: tx.timestamp ?? Date.now(),
      });
    }

    return incoming;
  } catch {
    return [];
  }
}

/**
 * Send XMR to a destination address.
 * Returns the transaction hash on success.
 */
export async function sendXmr(
  wallet: any,
  destinationAddress: string,
  amount: number, // piconero
): Promise<string> {
  if (!wallet) throw new Error('Wallet not initialized');

  const moneroTs = await getMoneroTs();

  const createdTx = await wallet.createTx({
    accountIndex: 0,
    address: destinationAddress,
    amount: BigInt(amount),
    relay: false, // Don't relay yet
  });

  // Confirm and relay
  const result = await wallet.relayTx(createdTx);

  return result.txHash ?? result.hash;
}

/**
 * Check the number of confirmations for a transaction.
 */
export async function getConfirmations(
  wallet: any,
  txHash: string,
): Promise<number> {
  if (!wallet) return 0;

  try {
    const tx = await wallet.getTx(txHash);
    return tx.confirmations ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Get the current daemon height.
 */
export async function getDaemonHeight(nodeUri: string): Promise<number> {
  const moneroTs = await getMoneroTs();

  try {
    const daemon = await moneroTs.connectToDaemonRpc({
      server: nodeUri,
      proxyToWorker: false,
    });
    const height = await daemon.getHeight();
    return height;
  } catch {
    return 0;
  }
}

/**
 * Close the wallet and free resources.
 */
export async function closeWallet(wallet: any): Promise<void> {
  if (!wallet) return;
  try {
    await stopBackgroundSync(wallet);
    await wallet.close();
  } catch {
    // Ignore
  }
}
